import { Injectable } from '@angular/core';
import { DynamicDialogRef } from 'primeng/dynamicdialog';

/**
 * Tracks all open PrimeNG DynamicDialogs so they can be closed atomically
 * when the session expires (or for any other global cleanup scenario).
 *
 * DynamicDialogs are appended to `<body>` and therefore survive Angular
 * route changes — this service ensures they are torn down before the
 * application navigates away (e.g. to /auth on session expiry).
 */
@Injectable({ providedIn: 'root' })
export class DynamicDialogCleanupService {

  private readonly openDialogs = new Set<DynamicDialogRef>();

  /**
   * Register a DynamicDialogRef so it can be closed during cleanup.
   * The ref is automatically unregistered when the dialog is closed
   * (either by the user or programmatically).
   */
  track(ref: DynamicDialogRef): void {
    this.openDialogs.add(ref);
    ref.onClose.subscribe(() => {
      this.openDialogs.delete(ref);
    });
  }

  /**
   * Close all tracked DynamicDialogs. Safe to call even if no dialogs
   * are open — it simply does nothing in that case.
   */
  closeAll(): void {
    const dialogs = Array.from(this.openDialogs);
    this.openDialogs.clear();
    dialogs.forEach((ref) => {
      try {
        ref.close();
      } catch {
        // Ignore — the dialog may already have been destroyed.
      }
    });
  }
}
